import React from "react";
import { Modal, Form, Input, Button, message } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useChangePasswordApiMutation } from "@/servicescustomerside/api/authcustomer/authApiCustomer";
import { selectCurrentCustomer } from "@/redux/slices/customerAuthSlice";
import { usePasswordStrength } from "@/hooks/usePasswordStrength";
import PasswordStrengthMeter from "@/components/UI/PasswordStrengthMeter";

const ChangePasswordModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const user = useSelector(selectCurrentCustomer);
  const [changePasswordApi, { isLoading }] = useChangePasswordApiMutation();
  const { passwordStrength, checkStrength, reset } = usePasswordStrength();
  const [newPassword, setNewPassword] = React.useState("");

  const handleClose = () => {
    form.resetFields();
    setNewPassword("");
    reset();
    onClose();
  };

  const onFinish = async (values) => {
    try {
      await changePasswordApi({
        email: user?.email,
        old_password: values.old_password,
        password: values.password,
      }).unwrap();

      message.success("Password changed successfully!");
      handleClose();
    } catch (error) {
      message.error(error?.data?.message || "Failed to change password");
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title="Change Password"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="mt-4">
        <Form.Item
          label="Current Password"
          name="old_password"
          rules={[
            { required: true, message: "Please enter your current password" },
          ]}
        >
          <Input.Password
            className="rounded-lg"
            iconRender={(visible) =>
              visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item
          label="New Password"
          name="password"
          rules={[
            { required: true, message: "Please enter a new password" },
            { min: 8, message: "Min 8 characters" },
          ]}
        >
          <Input.Password
            className="rounded-lg"
            onChange={(e) => {
              setNewPassword(e.target.value);
              checkStrength(e.target.value);
            }}
            iconRender={(visible) =>
              visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <PasswordStrengthMeter password={newPassword} strength={passwordStrength} />

        <Form.Item
          label="Confirm New Password"
          name="confirmPassword"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Please confirm your password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <Input.Password
            className="rounded-lg"
            iconRender={(visible) =>
              visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item className="mb-0 mt-6">
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isLoading}
            className="bg-[#F79F1A] hover:!bg-[#e08905] h-11 rounded-full font-bold border-none"
          >
            Change Password
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
